<template>
    <section
        :id="slice.primary.anchorName || undefined"
        ref="section"
        class="slice slice-features slice-features--with-background"
        aria-label="features"
    >
        <div class="slice-content">
            <div class="slice-features--with-background__panel">
                <div v-if="slice.primary.category" class="slice-features--with-background__category">
                    {{ slice.primary.category }}
                </div>
                <PrismicRichText v-if="slice.primary.title" :field="slice.primary.title" class="slice-title" />
                <PrismicRichText v-if="slice.primary.description" :field="slice.primary.description" class="slice-description" />

                <div class="slice-body slice-features--with-background__items">
                    <div v-for="(item, i) in slice.items" :key="i" class="slice-features--with-background__item">
                        <PrismicImage :field="item.image" class="slice-features--with-background__item-image" />
                        <div class="slice-features--with-background__item-content">
                            <div class="slice-features--with-background__item-title">{{ item.title }}</div>
                            <PrismicRichText :field="item.text" class="slice-features--with-background__item-text" />
                        </div>
                    </div>
                </div>

                <div v-if="cta" class="slice-footer">
                    <VJoyButton :href="$withLocalePrefix(cta.url)" :variant="cta.variant" data-component-source="add-on">
                        {{ cta.text }}
                    </VJoyButton>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {computed, ref} from 'vue';
    import {isFilled} from '@prismicio/helpers';
    import {getSliceComponentProps, PrismicImage, PrismicRichText} from '@prismicio/vue';
    import type {FeaturesSlice} from '@prismicio-types';
    import {useComponentTracking} from '#imports';
    import {VJoyButton} from '@maltjoy/core-vue';

    type FeaturesWithBackgroundInZone = Extract<FeaturesSlice, {variation: 'featuresWithBackground'}>;
    const props = defineProps(getSliceComponentProps<FeaturesWithBackgroundInZone>());

    const section = ref(null);

    type Cta = {url: string; text: string; variant: 'primary' | 'secondary'};

    const cta = computed<Cta | null>(() => {
        const link = props.slice.primary.cta;
        if (!isFilled.link(link)) {
            return null;
        }

        const filledLink = link as {url: string; text?: string; variant?: 'primary' | 'secondary'};
        return {
            url: filledLink.url,
            text: filledLink.text ?? '',
            variant: filledLink.variant ? 'primary' : 'secondary',
        };
    });

    useComponentTracking(section);
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .slice-features--with-background {
        .slice-description {
            max-width: none;
        }

        &__panel {
            text-align: left;
            border-radius: var(--joy-core-radius-4);
            background-color: var(--joy-color-secondary-10);
            padding: var(--joy-core-spacing-8) var(--joy-core-spacing-5);

            @include mq.screen_md(min) {
                padding: var(--joy-core-spacing-12) 80px;
            }
        }

        &__category {
            font-weight: var(--joy-font-weight-bold);
            color: var(--joy-color-secondary-30);
            margin-bottom: var(--joy-core-spacing-2);
        }

        &__items {
            @include mq.screen_md(min) {
                display: grid;
                column-gap: var(--joy-core-spacing-8);
                grid-template-columns: repeat(2, minmax(0, 1fr));
            }
        }

        &__item {
            display: flex;
            align-items: flex-start;
            gap: var(--joy-core-spacing-4);
            margin-bottom: var(--joy-core-spacing-8);

            &-image {
                width: 40px;
            }

            &-content {
                flex: 1 1 0%;
                width: 100%;
            }

            &-title {
                font-size: var(--joy-font-size-primary-600);
                font-weight: var(--joy-font-weight-bold);
            }

            &-text {
                margin-top: var(--joy-core-spacing-4);
            }
        }
    }
</style>
